import {
  Card,
  Col,
  Row,
  Spin,
  Tag,
  Typography,
} from "antd";
import {
  useGetNotificationQuery,
  useUpDateNotificationMutation,
} from "../../../feature/dashboard/dashboardApiSlice";
import {
  forwardRef,
  useImperativeHandle,
  useEffect,
  useState,
} from "react";
import { useAppDispatch } from "../../../app/hooks";
import { setNotifications } from "../../../feature/ui/uiSlice";
import { useNavigate } from "react-router-dom";
import { BellOutlined } from "@ant-design/icons";
import styled from "styled-components";

const { Title } = Typography;

// 💡 Styled components for empty state
const NoNotificationWrapper = styled.div`
  text-align: center;
  margin-top: 100px;
  color: #999;
`;

const BellIconStyled = styled(BellOutlined)`
  font-size: 64px;
  color: #ccc;
  margin-bottom: 16px;
`;

export type NotificationItemHandle = {
  markAllAsRead: () => void;
  hasUnread: () => boolean;
};

const NotificationItem = forwardRef<NotificationItemHandle>((_, ref) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { data, isLoading, isFetching } = useGetNotificationQuery();
  const [updateNotification] = useUpDateNotificationMutation();
  const [notifications, setNotificationsState] = useState<any[]>([]);

  useEffect(() => {
    if (data?.data) {
      setNotificationsState(data.data);
      dispatch(setNotifications(data.data));
    }
  }, [data, dispatch]);

  // ✅ Mark all unread notifications as read
  const markAllAsRead = async () => {
    const unread = notifications.filter((n) => n.status === true);

    await Promise.all(
      unread.map((n) =>
        updateNotification({ id: n.id, status: false }).unwrap()
      )
    );

    const updatedNotifications = notifications.map((n) => ({ ...n, status: false }));
    setNotificationsState(updatedNotifications);
    dispatch(setNotifications(updatedNotifications));
  };

  // ✅ Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    markAllAsRead,
    hasUnread: () => notifications.some((n) => n.status === true),
  }));

  const handleRedirect = (description: string) => {
    if (description.toLowerCase().includes("driver")) {
      navigate("/drivers");
    } else if (description.toLowerCase().includes("rider")) {
      navigate("/users");
    } else if (description.toLowerCase().includes("book")) {
      navigate("/bookings");
    }
  };

  // ✅ Render empty state when there are no notifications
  if (!isLoading && !isFetching && notifications.length === 0) {
    return (
      <NoNotificationWrapper>
        <BellIconStyled />
        <p>No new notifications</p>
      </NoNotificationWrapper>
    );
  }

  return (
    <>
      {
        !isLoading || isFetching ? data?.data.map((notification: any) => {
          return (
            <Card
              key={notification.id}
              style={{ backgroundColor: "#cbe3f7", marginBottom: "1%", cursor: "pointer" }}
              onClick={() => handleRedirect(notification.description)}
            >
              {
                notification.status === true && <Tag color="#14A011" style={{ fontSize: "8px" }}>
                  NEW
                </Tag>
              }
              <Row justify={"space-between"}>
                <Col>
                  <Title level={5} className="m-0">
                    <span>{notification.title}</span>
                  </Title>
                </Col>
                <Col>
                  <span>{notification.date}</span>
                </Col>
              </Row>
              <Row>
                <span>{notification.description}</span>
              </Row>
            </Card>
          );
        }) : <Spin size="large" style={{ display: "flex", justifyContent: "center" }} />
      }
    </>
  );
});

export default NotificationItem;
